import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBookingWizard } from 'core/contexts/bookingWizard';
import { useSearch } from 'core/contexts/search';
import { Form, FormSpy } from 'react-final-form';
import { validateEmail, validateRequiredString } from '@utriper/core';
import { AlertDialog, Stepper } from '@utriper/core-ui';
import { Box, Grid2, useMediaQuery } from '@mui/material';
import SelectActivityForm from './components/SelectActivityForm';
import TriperForm from './components/TriperForm';
import BookingDetails from './components/BookingDetails';
import PaymentForm from './components/PaymentForm';
import SuccessPage from './components/SuccessPage';
import ActivityForm from './components/ActivityForm';

const BookingForm = ({ activeStep, form }) => {
    switch (activeStep) {
        case 0:
            return <SelectActivityForm form={form} />;
        case 1:
            return <TriperForm form={form} />;
        case 2:
            return <BookingDetails />;
        case 3:
            return <PaymentForm form={form} />;
        default:
            return null;
    }
};

const BookingNewPage = () => {
    const { t: translate } = useTranslation();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { steps, activeStep, contractId, setContractId, handleOpenAlert, openAlert, isSuccess, resetWizard, itemToEdit, handleUpsertCheckoutItem, handleCloseEdit, itemToDelete, handleRemoveCheckoutItem, handleCloseDelete } = useBookingWizard();
    const { search, searchFilterDebounce, setSearchValue } = useSearch();

    useEffect(() => {
        search(contractId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFilterDebounce, contractId]);

    useEffect(() => {
        resetWizard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess]);

    const title = translate('booking_wizard.title');

    // COPIED FROM OLD CHECKOUT
    const validatePhone = (value) => (value && /^(?:(?:\+|00)88|01)?\d{11}$/i.test(value) ? undefined : 'Número de teléfono');

    const requiredStringValues = ['firstName', 'lastName', 'email', 'mobilePhone'];
    const validateForm = (values) => {
        const errors = {};

        for (const key of requiredStringValues) {
            if (!validateRequiredString(values?.[key])) {
                errors[key] = 'Requerido';
            }
        }

        if (!validateEmail(values?.email)) {
            errors.email = 'Email invalido!';
        }

        if (!validatePhone(values?.mobilePhone)) {
            errors.mobilePhone = 'Número de teléfono invalido!';
        }

        return errors;
    };

    if (isSuccess) {
        return <SuccessPage />;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', gap: '2rem', width: '100%', height: '100dvh', overflow: 'hidden' }}>
            {isMobile ? (
                <Stepper title={title} steps={steps} activeStep={activeStep} />
            ) : (
                <Box sx={{ width: '100%', p: 2, boxSizing: 'border-box' }}>
                    <Stepper title={title} steps={steps} activeStep={activeStep} variant="horizontal" />
                </Box>
            )}

            {/* The page is pinned to the viewport, so this is where a step taller than the screen scrolls (#501).
                minHeight 0 lets the flex item shrink to the space left; without it it grows to its content and is clipped. */}
            <Grid2 container sx={{ width: '100%', p: 2, pt: isMobile ? '6rem' : 2, flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Form
                    onSubmit={() => {}}
                    validate={validateForm}
                    render={({ handleSubmit, form }) => (
                        <form onSubmit={handleSubmit} noValidate style={{ width: '100%', height: '100%', boxSizing: 'border-box', padding: 8 }}>
                            <BookingForm activeStep={activeStep} form={form} />

                            <FormSpy
                                subscription={{ values: true }}
                                onChange={({ values }) => {
                                    const { search = '', contractId = null } = values;

                                    setSearchValue(search);
                                    setContractId(contractId);
                                }}
                            />
                        </form>
                    )}
                />
            </Grid2>

            <ActivityForm open={itemToEdit?.id} initialItem={itemToEdit} handleSubmit={handleUpsertCheckoutItem} handleClose={handleCloseEdit} labelNext="Actualizar" />

            <AlertDialog
                open={itemToDelete?.id}
                handleClose={handleCloseDelete}
                handleSubmit={() => {
                    handleRemoveCheckoutItem(itemToDelete?.id);
                    handleCloseDelete();
                }}
                dialogTitle="Eliminar Actividad"
                dialogContent={`Estas seguro de eliminar la actividad: ${itemToDelete?.name}`}
                labelBack={'Cancelar'}
                labelNext={'Aceptar'}
                maxWidth="sm"
                fullWidth
            />

            <AlertDialog
                open={openAlert}
                handleClose={() => handleOpenAlert()}
                handleSubmit={() => handleOpenAlert()}
                dialogTitle="Volviste al Inicio del Proceso"
                dialogContent="
                No quedan experiencias ni paquetes seleccionados en tu checkout.

                Por eso te redirigimos aquí para que puedas continuar con el proceso de reserva!
                "
                hideBack
                labelNext={'Aceptar'}
                maxWidth="sm"
                fullWidth
            />
        </Box>
    );
};

export default BookingNewPage;
